import { $ } from "../lib/dom";
import { type Cell, key } from "../lib/util";
import { closeDialog, openDialog } from "./dialog";
import { closeHelp, helpOpen, openHelp } from "./help";
import { L, S } from "./state";
import { renderHUD } from "./time";
import type { Dir, Move } from "./types";
import { bfs, COLS, interact, interactCells, passable, ROWS } from "./world";
import { closeZoom, isZoomOpen, openZoom, zoomKey } from "./zoom";

export interface Player {
  x: number;
  y: number;
  fx: number;
  fy: number;
  dir: Dir;
  path: Cell[];
  moving: Move | null;
  /** Interaction to trigger on arrival. */
  pending: string | null;
}

export const P: Player = { x: 0, y: 0, fx: 0, fy: 0, dir: "down", path: [], moving: null, pending: null };

export const DIRV: Record<Dir, Cell> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };

export const dirTo = (fx: number, fy: number, tx: number, ty: number): Dir =>
  tx > fx ? "right" : tx < fx ? "left" : ty > fy ? "down" : "up";

/** Held direction keys, most recent last. */
const keys = new Set<Dir>();

export function clearMovement(): void {
  keys.clear();
  P.path = [];
  P.pending = null;
}

export function placePlayer([x, y]: Cell): void {
  P.x = P.fx = x;
  P.y = P.fy = y;
  P.dir = "down";
  P.moving = null;
  clearMovement();
}

function adjacentGoal(id: string): (x: number, y: number) => boolean {
  const cells = interactCells(id);
  return (x, y) => cells.some(([a, b]) => Math.abs(a - x) + Math.abs(b - y) === 1);
}

function goInteract(id: string): void {
  const g = adjacentGoal(id);
  if (g(P.x, P.y)) {
    faceTo(id);
    triggerInteract(id);
    return;
  }
  const path = bfs(P.x, P.y, g);
  if (!path) return;
  P.path = path;
  P.pending = id;
}

function faceTo(id: string): void {
  for (const [x, y] of interactCells(id))
    if (Math.abs(x - P.x) + Math.abs(y - P.y) === 1) {
      P.dir = dirTo(P.x, P.y, x, y);
      return;
    }
}

export function triggerInteract(id: string): void {
  if (!S.started || S.cine) return;
  if (L.beforeInteract?.(id) === false) return;
  const quick = L.quick?.[id];
  if (quick) {
    quick();
    renderHUD();
    return;
  }
  openDialog(id);
  const zoom = L.zooms?.[id];
  if (zoom && S.dialog === id) {
    const z = zoom();
    if (z) openZoom({ dlg: id, ...z });
  }
}

/** The interaction in front of the player (or any adjacent one), with its cell. */
export function facingInteract(): [id: string, x: number, y: number] | null {
  const [dx, dy] = DIRV[P.dir];
  const ahead = interact.get(key(P.x + dx, P.y + dy));
  if (ahead) return [ahead, P.x + dx, P.y + dy];
  for (const [ax, ay] of Object.values(DIRV)) {
    const id = interact.get(key(P.x + ax, P.y + ay));
    if (id) return [id, P.x + ax, P.y + ay];
  }
  return null;
}

export function stepTo(nx: number, ny: number): boolean {
  if (!passable(nx, ny)) return false;
  if (isZoomOpen()) closeZoom();
  P.dir = dirTo(P.x, P.y, nx, ny);
  P.moving = { from: [P.x, P.y], to: [nx, ny], start: performance.now() };
  P.x = nx;
  P.y = ny;
  if (S.dialog && !L.dialogs[S.dialog]?.keepOpen) closeDialog();
  L.onStep?.(nx, ny);
  return true;
}

/** Advance the player one step along a click path / pending interaction / held key. */
export function updatePlayerIntent(): void {
  const next = P.path.shift();
  if (next) {
    if (!stepTo(...next)) P.path = [];
  } else if (P.pending) {
    const id = P.pending;
    P.pending = null;
    faceTo(id);
    triggerInteract(id);
  } else if (keys.size) {
    const d = [...keys].pop() as Dir;
    const [vx, vy] = DIRV[d];
    if (!stepTo(P.x + vx, P.y + vy)) P.dir = d;
  }
}

/** Arrow keys and WASD → direction. */
export const KEYMAP: Readonly<Record<string, Dir>> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  w: "up",
  s: "down",
  a: "left",
  d: "right",
  W: "up",
  S: "down",
  A: "left",
  D: "right",
};

const isHelpKey = (k: string): boolean => k === "h" || k === "H";

/* ---------- actions shared by keyboard and the touch pad ---------- */

/** Player can act on the map (no cutscene, overlay or finished level in the way). */
export const canControl = (): boolean =>
  S.live && S.started && !S.cine && !helpOpen() && !$("#debrief").classList.contains("show");

/** Start holding a direction; cancels click-to-walk. */
export function pressDir(d: Dir): void {
  keys.add(d);
  P.path = [];
  P.pending = null;
}

export function releaseDir(d: Dir): void {
  keys.delete(d);
}

/** Space / A button: interact with what's in front. Returns true if something was triggered. */
export function interactAhead(): boolean {
  const f = facingInteract();
  if (!f) return false;
  triggerInteract(f[0]);
  return true;
}

/** Esc / B button: close the close-up first, then the dialog. */
export function back(): void {
  if (isZoomOpen()) closeZoom(true);
  else if (S.dialog) closeDialog();
}

export function initPlayerInput(): void {
  document.addEventListener("keydown", (e) => {
    if (helpOpen()) {
      if (e.key === "Escape" || isHelpKey(e.key)) {
        closeHelp();
        e.preventDefault();
      }
      return;
    }
    const target = e.target as HTMLElement | null;
    if (!S.live || !S.started || target?.tagName === "TEXTAREA" || $("#debrief").classList.contains("show") || S.cine)
      return;
    if ((isHelpKey(e.key) || e.key === "?") && !isZoomOpen()) {
      openHelp();
      e.preventDefault();
      return;
    }
    if (isZoomOpen()) {
      if (zoomKey(e) || KEYMAP[e.key]) e.preventDefault();
      return;
    }
    const m = KEYMAP[e.key];
    if (m) {
      pressDir(m);
      e.preventDefault();
    }
    if (e.key === " " || e.key === "Enter" || e.key === "e" || e.key === "E") {
      if (document.activeElement?.tagName === "BUTTON") return;
      if (interactAhead()) e.preventDefault();
    }
    if (e.key === "Escape") back();
  });
  document.addEventListener("keyup", (e) => {
    const m = KEYMAP[e.key];
    if (m) releaseDir(m);
  });
  // A key released while the window is unfocused never fires keyup; don't keep walking.
  addEventListener("blur", () => keys.clear());
  $("#cv").addEventListener("click", (e) => {
    if (!S.live || !S.started || S.cine) return;
    if (isZoomOpen()) closeZoom();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const tx = Math.floor(((e.clientX - r.left) / r.width) * COLS);
    const ty = Math.floor(((e.clientY - r.top) / r.height) * ROWS);
    const id = interact.get(key(tx, ty));
    if (id) {
      goInteract(id);
      return;
    }
    if (passable(tx, ty)) {
      const p = bfs(P.x, P.y, (x, y) => x === tx && y === ty);
      if (p) {
        P.path = p;
        P.pending = null;
      }
    }
  });
}
